import React, {
  PropsWithChildren,
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { useLocation } from 'react-router';
import useAnalyticsContext from '|/hooks/use-analytics-context';

export interface EventMetaData {
  metaData1?: string;
  metaData2?: string;
  metaData3?: string;
  metaData4?: string;
}

enum Modals {
  TradeModal = 'trade-modal',
  CancelOrderModal = 'cancel-order-modal',
  CancelOrderModalDS = 'cancel-order-modal-ds',
  EditOrderModal = 'edit-order-modal',
  EditOrderModalDS = 'edit-order-modal-ds',
  CardEntryModal = 'card-entry-modal',
  LinkPlaidModal = 'link-plaid-modal',
  ExplainPlaidModal = 'explain-plaid-modal',
  BankAccountManualEntryModal = 'bank-account-manual-entry-modal',
  DownPaymentCreditModal = 'down-payment-credit-modal',
  DeclineAutoPayModal = 'decline-auto-pay-modal',
  DLViewModal = 'view-modal',
  PersonaConnectingModal = 'persona-connecting-modal',
  PersonaConnectingErrorModal = 'connection-error-modal',
  DriversLicenseUploadFallbackModal = 'drivers-license-upload-fallback-modal',
  NameMatchingModal = 'name-matching-modal',
  CustomizeTermsModal = 'customize-terms-modal',
  BuildDealTradeInTaxSavingsModal = 'build-deal-trade-in-tax-savings-modal',
  CoSignerModal = 'cosigner-modal',
  PrimaryFraudModal = 'primary-fraud-modal',
  CosignerFraudModal = 'cosigner-fraud-modal',
  CoApplicantUnableToAddModal = 'co-applicant-unable-to-add-modal',
  IsIneligibleVehicleModal = 'is-ineligible-vehicle-modal',
  EditPaymentModal = 'edit-payment-modal',
  LockTimerModal = 'lock-timer-modal',
  ExpiredTradeModal = 'expired-trade-modal',
  OrderSummaryModal = 'order-summary-modal',
  KiqExitWarningModal = 'identity-verification-exit-warning-modal',
  KiqExitWarningNavRailModal = 'identity-verification-exit-warning-nav-rail-modal',
  VinEtchingModal = 'vin-etching-modal',
  TradePayoffUpdateModal = 'trade-payoff-update-modal',
  GapCoverageRemovedModal = 'gap-coverage-removed-modal',
  GapCoverageDetailsModal = 'gap-coverage-details-modal',
  PuodInvalidVehicleHubModal = 'puod-invalid-vehicle-hub-modal',
  ExchangeModal = 'exchange-modal',
  AddressNormalizationModal = 'address-normalization-modal',
  MailingAddressWarningModal = 'mailing-address-warning-modal',
  CosignerNotAllowedModal = 'cosigner-not-allowed-modal',
  SupportModal = 'support-modal',
  TransferMethodModal = 'transfer-method-modal',
  ProtectionCoverageModal = 'protection-coverage-modal',
  ProtectionCustomizeModal = 'protection-customize-modal',
  ProtectionEstimateModal = 'protection-estimate-modal',
  ProtectionLearnMoreModal = 'protection-learn-more-modal',
  ProtectionRecommendedModal = 'protection-recommended-modal',
  IncompleteStepsModal = 'incomplete-steps-modal'
}

/**
 * @deprecated Use EventedModal instead of the global ModalContext.
 *
 * The global ModalContext system is deprecated due to:
 * - Modal state conflicts preventing multiple modals from working together
 * - Complex state synchronization between global and local modal systems
 *
 * Migrate to EventedModal pattern:
 * - Use EventedModal component instead of useModal hook
 * - Pass simple boolean isOpen prop instead of global state
 * - Add proper analytics ID from ModalNames enum
 * - Remove global modal context dependencies
 *
 * See payment-options step modals for examples of the new pattern.
 * Use the /convert-to-evented-modal slash command to migrate existing modals.
 */
const defaultValue = {
  currentModal: null as null | Modals,
  isTradeModalShown: false,
  isCancelOrderModalShown: false,
  isCancelOrderModalDSShown: false,
  isEditOrderModalShown: false,
  isEditOrderModalDSShown: false,
  isCardEntryModalShown: false,
  isLinkPlaidModalShown: false,
  isExplainPlaidModalShown: false,
  isBankAccountManualEntryModalShown: false,
  isDownPaymentCreditModalShown: false,
  isDeclineAutoPayModalShown: false,
  isDLViewModalShown: false,
  isPersonaConnectingModalShown: false,
  isPersonaConnectingErrorModalShown: false,
  isDriversLicenseUploadFallbackModalShown: false,
  isNameMatchingModalShown: false,
  isCustomizeTermsModalShown: false,
  isBuildDealTradeInTaxSavingsModalShown: false,
  isCosignerModalShown: false,
  isPrimaryFraudModalShown: false,
  isCosignerFraudModalShown: false,
  isCoApplicantUnableToAddModalShown: false,
  isExpiredTradeModalShown: false,
  isIneligibleVehicleModalShown: false,
  isLockTimerModalShown: false,
  isOrderSummaryModalShown: false,
  isEditPaymentModalShown: false,
  isKiqExitWarningModalShown: false,
  isKiqExitWarningModalNavRailShown: false,
  isVinEtchingModalShown: false,
  isGapCoverageRemovedModalShown: false,
  isGapCoverageDetailsModalShown: false,
  isTradePayoffUpdateModalShown: false,
  isPuodInvalidVehicleHubModalShown: false,
  isExchangeModalShown: false,
  isAddressNormalizationModalShown: false,
  isMailingAddressWarningModalShown: false,
  isCosignerNotAllowedModalShown: false,
  isIncompleteStepsModalShown: false,
  isTransferMethodModalShown: false,
  isProtectionCoverageModalShown: false,
  isProtectionCustomizeModalShown: false,
  isProtectionEstimateModalShown: false,
  isProtectionLearnMoreModalShown: false,
  isProtectionRecommendedModalShown: false,
  isSupportModalShown: false,
  setTradeInModalOpen: (_: boolean) => {
    /* noop */
  },
  setCancelOrderModalOpen: (_: boolean) => {
    /* noop */
  },
  setEditOrderModalOpen: (_: boolean) => {
    /* noop */
  },
  setCardEntryModalOpen: (_: boolean) => {
    /* noop */
  },
  setLinkPlaidModalOpen: (_: boolean) => {
    /* noop */
  },
  setExplainPlaidModalOpen: (_: boolean) => {
    /* noop */
  },
  setBankAccountManualEntryModalOpen: (_: boolean) => {
    /* noop */
  },
  setDownPaymentCreditModalOpen: (_: boolean) => {
    /* noop */
  },
  setDeclineAutoPayModalOpen: (_: boolean) => {
    /* noop */
  },
  setDLViewModalOpen: (_: boolean) => {
    /* noop */
  },
  setPersonaConnectingModalOpen: (_: boolean) => {
    /* noop */
  },
  setPersonaConnectingErrorModalOpen: (_: boolean) => {
    /* noop */
  },
  setDriversLicenseUploadFallbackModalOpen: (_: boolean) => {
    /* noop */
  },
  setNameMatchingModalOpen: (_: boolean) => {
    /* noop */
  },
  setCustomizeTermsModalOpen: (_: boolean) => {
    /* noop */
  },
  setBuildDealTradeInTaxSavingsModalOpen: (_: boolean) => {
    /* noop */
  },
  setCosignerModalOpen: (_: boolean) => {
    /* noop */
  },
  setPrimaryFraudModalOpen: (_: boolean) => {
    /* noop */
  },
  setCosignerFraudModalOpen: (_: boolean) => {
    /* noop */
  },
  setCoApplicantUnableToAddModalOpen: (_: boolean) => {
    /* noop */
  },
  setIsIneligibleVehicleModalOpen: (_: boolean) => {
    /* noop */
  },
  setEditPaymentModalOpen: (_: boolean) => {
    /* noop */
  },
  setExpiredTradeModalOpen: (_: boolean) => {
    /* noop */
  },
  setIsOrderSummaryModalOpen: (_: boolean) => {
    /* noop */
  },
  setKiqExitWarningModalOpen: (_: boolean) => {
    /* noop */
  },
  setKiqExitWarningModalNavRailOpen: (_: boolean) => {
    /* noop */
  },
  setIsVinEtchingModalOpen: (_: boolean) => {
    /* noop */
  },
  setIsTradePayoffUpdateModalOpen: (_: boolean) => {
    /* noop */
  },
  setGapCoverageRemovedModalOpen: (_: boolean) => {
    /* noop */
  },
  setGapCoverageDetailsModalOpen: (_: boolean) => {
    /* noop */
  },
  setPuodInvalidVehicleHubModalOpen: (_: boolean) => {
    /* noop */
  },
  setExchangeModalOpen: (_: boolean) => {
    /* noop */
  },
  setAddressNormalizationModalOpen: (_: boolean) => {
    /* noop */
  },
  setMailingAddressWarningModalOpen: (_: boolean) => {
    /* noop */
  },
  setCosignerNotAllowedModalOpen: (_: boolean) => {
    /* noop */
  },
  setTransferMethodModalOpen: (_: boolean) => {
    /* noop */
  },
  setProtectionCoverageModalShown: (_: boolean) => {
    /* noop */
  },
  setProtectionCustomizeModalShown: (_: boolean) => {
    /* noop */
  },
  setProtectionLearnMoreModalShown: (_: boolean) => {
    /* noop */
  },
  setProtectionEstimateModalShown: (_: boolean) => {
    /* noop */
  },
  setProtectionRecommendedModalShown: (_: boolean) => {
    /* noop */
  },
  setLockTimerModalOpen: (_: boolean) => {
    /* noop */
  },
  setSupportModalOpen: (_: boolean) => {
    /* noop */
  },
  setEditOrderModalDSOpen: (_: boolean) => {
    /* noop */
  },
  setCancelOrderModalDSOpen: (_: boolean) => {
    /* noop */
  },
  setIncompleteStepsModalOpen: (_: boolean) => {
    /* noop */
  }
};

/**
 * @deprecated Use EventedModal instead. See defaultValue JSDoc above for migration details.
 */
const ModalContext = createContext(defaultValue);

/**
 * @deprecated Use EventedModal instead. See defaultValue JSDoc above for migration details.
 */
const ModalContextProvider: React.FC<PropsWithChildren<Record<string, never>>> = ({ children }) => {
  const [currentModal, setCurrentModal] = useState(null as null | Modals);
  const { trackEvent } = useAnalyticsContext();
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);

  const closeModal = useCallback(() => {
    trackEvent('pp_modal_closed', { modal: currentModal });
    setCurrentModal(null);
  }, [currentModal, trackEvent, setCurrentModal]);

  useEffect(() => {
    if (location.pathname !== previousPathnameRef.current && currentModal !== null) {
      closeModal();
    }
    previousPathnameRef.current = location.pathname;
  }, [location.pathname, currentModal, closeModal]);

  const showModal = useCallback(
    (modal: Modals) => {
      if (currentModal) {
        closeModal();
      }
      trackEvent('pp_modal_opened', { modal });
      setCurrentModal(modal);
    },
    [currentModal, closeModal, trackEvent, setCurrentModal]
  );

  const setTradeInModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.TradeModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCancelOrderModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CancelOrderModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCancelOrderModalDSOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CancelOrderModalDS) : closeModal();
    },
    [showModal, closeModal]
  );

  const setEditOrderModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.EditOrderModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setEditOrderModalDSOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.EditOrderModalDS) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCardEntryModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CardEntryModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setLinkPlaidModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.LinkPlaidModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setExplainPlaidModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ExplainPlaidModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setBankAccountManualEntryModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.BankAccountManualEntryModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setDownPaymentCreditModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.DownPaymentCreditModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setDeclineAutoPayModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.DeclineAutoPayModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setDLViewModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.DLViewModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setPersonaConnectingModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.PersonaConnectingModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setPersonaConnectingErrorModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.PersonaConnectingErrorModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setDriversLicenseUploadFallbackModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.DriversLicenseUploadFallbackModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setNameMatchingModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.NameMatchingModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCustomizeTermsModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CustomizeTermsModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setBuildDealTradeInTaxSavingsModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.BuildDealTradeInTaxSavingsModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCosignerModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CoSignerModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setPrimaryFraudModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.PrimaryFraudModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCosignerFraudModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CosignerFraudModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCoApplicantUnableToAddModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CoApplicantUnableToAddModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setExpiredTradeModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ExpiredTradeModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setIsIneligibleVehicleModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.IsIneligibleVehicleModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setIsOrderSummaryModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.OrderSummaryModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setKiqExitWarningModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.KiqExitWarningModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setKiqExitWarningModalNavRailOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.KiqExitWarningNavRailModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setEditPaymentModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.EditPaymentModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setIsVinEtchingModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.VinEtchingModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setGapCoverageRemovedModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.GapCoverageRemovedModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setGapCoverageDetailsModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.GapCoverageDetailsModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setIsTradePayoffUpdateModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.TradePayoffUpdateModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setExchangeModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ExchangeModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setPuodInvalidVehicleHubModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.PuodInvalidVehicleHubModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setAddressNormalizationModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.AddressNormalizationModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setMailingAddressWarningModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.MailingAddressWarningModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setCosignerNotAllowedModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.CosignerNotAllowedModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setProtectionCoverageModalShown = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ProtectionCoverageModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setProtectionEstimateModalShown = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ProtectionEstimateModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setProtectionRecommendedModalShown = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ProtectionRecommendedModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setProtectionLearnMoreModalShown = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ProtectionLearnMoreModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setProtectionCustomizeModalShown = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.ProtectionCustomizeModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setTransferMethodModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.TransferMethodModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setLockTimerModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.LockTimerModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setSupportModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.SupportModal) : closeModal();
    },
    [showModal, closeModal]
  );

  const setIncompleteStepsModalOpen = useCallback(
    (isOpen: boolean) => {
      isOpen ? showModal(Modals.IncompleteStepsModal) : closeModal();
    },
    [showModal, closeModal]
  );

  return (
    <ModalContext.Provider
      value={{
        currentModal,
        isTradeModalShown: currentModal === Modals.TradeModal,
        isCancelOrderModalShown: currentModal === Modals.CancelOrderModal,
        isCancelOrderModalDSShown: currentModal === Modals.CancelOrderModalDS,
        isEditOrderModalShown: currentModal === Modals.EditOrderModal,
        isEditOrderModalDSShown: currentModal === Modals.EditOrderModalDS,
        isCardEntryModalShown: currentModal === Modals.CardEntryModal,
        isLinkPlaidModalShown: currentModal === Modals.LinkPlaidModal,
        isExplainPlaidModalShown: currentModal === Modals.ExplainPlaidModal,
        isBankAccountManualEntryModalShown: currentModal === Modals.BankAccountManualEntryModal,
        isDownPaymentCreditModalShown: currentModal === Modals.DownPaymentCreditModal,
        isDeclineAutoPayModalShown: currentModal === Modals.DeclineAutoPayModal,
        isDLViewModalShown: currentModal === Modals.DLViewModal,
        isPersonaConnectingModalShown: currentModal === Modals.PersonaConnectingModal,
        isPersonaConnectingErrorModalShown: currentModal === Modals.PersonaConnectingErrorModal,
        isDriversLicenseUploadFallbackModalShown:
          currentModal === Modals.DriversLicenseUploadFallbackModal,
        isNameMatchingModalShown: currentModal === Modals.NameMatchingModal,
        isCustomizeTermsModalShown: currentModal === Modals.CustomizeTermsModal,
        isBuildDealTradeInTaxSavingsModalShown:
          currentModal === Modals.BuildDealTradeInTaxSavingsModal,
        isCosignerModalShown: currentModal === Modals.CoSignerModal,
        isPrimaryFraudModalShown: currentModal === Modals.PrimaryFraudModal,
        isCosignerFraudModalShown: currentModal === Modals.CosignerFraudModal,
        isCoApplicantUnableToAddModalShown: currentModal === Modals.CoApplicantUnableToAddModal,
        isExpiredTradeModalShown: currentModal === Modals.ExpiredTradeModal,
        isIneligibleVehicleModalShown: currentModal === Modals.IsIneligibleVehicleModal,
        isOrderSummaryModalShown: currentModal === Modals.OrderSummaryModal,
        isEditPaymentModalShown: currentModal === Modals.EditPaymentModal,
        isKiqExitWarningModalShown: currentModal === Modals.KiqExitWarningModal,
        isKiqExitWarningModalNavRailShown: currentModal === Modals.KiqExitWarningNavRailModal,
        isVinEtchingModalShown: currentModal === Modals.VinEtchingModal,
        isGapCoverageRemovedModalShown: currentModal === Modals.GapCoverageRemovedModal,
        isGapCoverageDetailsModalShown: currentModal === Modals.GapCoverageDetailsModal,
        isTradePayoffUpdateModalShown: currentModal === Modals.TradePayoffUpdateModal,
        isPuodInvalidVehicleHubModalShown: currentModal === Modals.PuodInvalidVehicleHubModal,
        isExchangeModalShown: currentModal === Modals.ExchangeModal,
        isAddressNormalizationModalShown: currentModal === Modals.AddressNormalizationModal,
        isMailingAddressWarningModalShown: currentModal === Modals.MailingAddressWarningModal,
        isCosignerNotAllowedModalShown: currentModal === Modals.CosignerNotAllowedModal,
        isProtectionCoverageModalShown: currentModal === Modals.ProtectionCoverageModal,
        isProtectionCustomizeModalShown: currentModal === Modals.ProtectionCustomizeModal,
        isProtectionEstimateModalShown: currentModal === Modals.ProtectionEstimateModal,
        isProtectionLearnMoreModalShown: currentModal === Modals.ProtectionLearnMoreModal,
        isProtectionRecommendedModalShown: currentModal === Modals.ProtectionRecommendedModal,
        isTransferMethodModalShown: currentModal === Modals.TransferMethodModal,
        isLockTimerModalShown: currentModal === Modals.LockTimerModal,
        isSupportModalShown: currentModal === Modals.SupportModal,
        isIncompleteStepsModalShown: currentModal === Modals.IncompleteStepsModal,
        setTradeInModalOpen,
        setCancelOrderModalOpen,
        setCancelOrderModalDSOpen,
        setEditOrderModalOpen,
        setEditOrderModalDSOpen,
        setCardEntryModalOpen,
        setLinkPlaidModalOpen,
        setExplainPlaidModalOpen,
        setBankAccountManualEntryModalOpen,
        setDownPaymentCreditModalOpen,
        setDeclineAutoPayModalOpen,
        setDLViewModalOpen,
        setPersonaConnectingModalOpen,
        setPersonaConnectingErrorModalOpen,
        setDriversLicenseUploadFallbackModalOpen,
        setNameMatchingModalOpen,
        setCustomizeTermsModalOpen,
        setBuildDealTradeInTaxSavingsModalOpen,
        setCosignerModalOpen,
        setPrimaryFraudModalOpen,
        setCosignerFraudModalOpen,
        setCoApplicantUnableToAddModalOpen,
        setIsIneligibleVehicleModalOpen,
        setIsOrderSummaryModalOpen,
        setEditPaymentModalOpen,
        setExpiredTradeModalOpen,
        setKiqExitWarningModalOpen,
        setKiqExitWarningModalNavRailOpen,
        setIsVinEtchingModalOpen,
        setGapCoverageRemovedModalOpen,
        setGapCoverageDetailsModalOpen,
        setIsTradePayoffUpdateModalOpen,
        setPuodInvalidVehicleHubModalOpen,
        setExchangeModalOpen,
        setAddressNormalizationModalOpen,
        setMailingAddressWarningModalOpen,
        setCosignerNotAllowedModalOpen,
        setLockTimerModalOpen,
        setSupportModalOpen,
        setTransferMethodModalOpen,
        setProtectionCoverageModalShown,
        setProtectionEstimateModalShown,
        setProtectionRecommendedModalShown,
        setProtectionLearnMoreModalShown,
        setProtectionCustomizeModalShown,
        setIncompleteStepsModalOpen
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

/**
 * @deprecated Use EventedModal instead. See deprecation notes above.
 */
export default ModalContext;

/**
 * @deprecated Use EventedModal instead. See deprecation notes above.
 */
export { ModalContextProvider };
export * from './modal-events';
export { default as EventedModal } from './evented-modal';
